import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import moment from "moment";
import { Container, Col, Row } from "reactstrap";
import { getAllUnitHolders } from "./../../stores/services/unit-holder.service";
import { getFunds } from "./../../stores/services/funds.service";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useHistory } from "react-router-dom";
import { BsTable } from "react-icons/bs";
import { FaFilter } from "react-icons/fa";
import Select from "react-select";
import { useCustomStylesDropDown } from "../../assets/css/customStyle";
import { exportToTxt, generateExcel } from "../../utils/customFunction";
import ReportTemplates from "../../components/ReportTemplate/ReportTemplate";
import ReportHeader from "../reports/report-header";

interface BalanceUnitEntry {
  fund_code: string;
  fund_unit?: string;
  units?: string;
  unit?: string;
  balance?: string;
  nav: number;
  investment: number;
  [key: string]: any;
}

interface UnitHolder {
  folio_no: string;
  name: string;
  cnic: string;
  ntn: string;
  city: string;
  address: string;
  province: string;
  phone: string;
  mobile: string;
  email: string;
  created_at: string;
  status: string;
  balance_unit: string;
  tax_status: string;
  tax_exempt: string;
  zakat_exempt: string;
  reinvest: boolean | string;
  type: string;
  risk_factor: string;
  [key: string]: any;
}

interface DisplayRow {
  s_no: number;
  fund_name: string;
  folio_no: string;
  name: string;
  category: string;
  units_held: string;
  acc_title: string;
  account_no: string;
  email: string;
  phone: string;
  cnic: string;
  ntn: string;
  tax_categ: string;
  tax_status: string;
  zakat_status: string;
  acc_taxfiler: string;
  dividend_option: string;
  cgt_status: string;
  account_status: string;
  account_type: string;
  zakat_status2: string;
  risk: string;
  address: string;
  province: string;
  city: string;
  _raw: UnitHolder;
}

interface FundOption {
  label: string;
  value: string;
}

const ALL_FUNDS_OPTION: FundOption = { label: "All", value: "" };

const BULK_PAGE_SIZE = 500;
const LOAD_CONCURRENCY = 10;
const DISPLAY_PAGE_SIZE = 10;
const EXPORT_CONCURRENCY = 10;

const CSV_HEADERS = [
  "S.No",
  "FundName",
  "Folio #",
  "Name",
  "Category",
  "Number of units held",
  "ACC_TITLE",
  "Account Number",
  "Email",
  "Phone #",
  "cnic",
  "ntn",
  "Tax Categ.",
  "TAXSTATUS",
  "ZAKATSTATUS",
  "ACC_TAXFILER",
  "DividendOption",
  "CGTStatus",
  "Account Status",
  "AccountType",
  "Zakat Status",
  "Risk",
  "Address",
  "Province",
  "City",
];

const COLUMN_HEADING = [
  { label: "S.No", value: "S.No" },
  { label: "Fund Name", value: "Fund Name" },
  { label: "Folio #", value: "Folio #" },
  { label: "Name", value: "Name" },
  { label: "Category", value: "Category" },
  { label: "Units Held", value: "Units Held" },
  { label: "ACC_TITLE", value: "ACC_TITLE" },
  { label: "Account No", value: "Account No" },
  { label: "Email", value: "Email" },
  { label: "Phone #", value: "Phone #" },
  { label: "CNIC", value: "CNIC" },
  { label: "NTN", value: "NTN" },
  { label: "Tax Categ.", value: "Tax Categ." },
  { label: "Tax Status", value: "Tax Status" },
  { label: "Zakat Status", value: "Zakat Status" },
  { label: "ACC_TAXFILER", value: "ACC_TAXFILER" },
  { label: "Dividend Option", value: "Dividend Option" },
  { label: "CGT Status", value: "CGT Status" },
  { label: "Account Status", value: "Account Status" },
  { label: "Account Type", value: "Account Type" },
  { label: "Risk", value: "Risk" },
  { label: "Address", value: "Address" },
  { label: "Province", value: "Province" },
  { label: "City", value: "City" },
];

const sanitizeFileName = (name: string) =>
  name.replace(/[^a-zA-Z0-9_\-]/g, "_");

const escapeCsvCell = (value: any): string => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  return str.includes(",") || str.includes('"') || str.includes("\n")
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

const parseBalanceUnit = (raw: string): BalanceUnitEntry[] => {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
  } catch {}
  return [];
};

const resolveFundUnit = (entry: BalanceUnitEntry): string => {
  const raw =
    entry.fund_unit ?? entry.units ?? entry.unit ?? entry.balance ?? null;
  if (raw === null || raw === undefined) return "";
  return String(raw);
};

const truncateAddress = (address: string): string => {
  if (!address) return "-";
  const words = address.trim().split(/\s+/);
  if (words.length <= 4) return address;
  return words.slice(0, 4).join(" ") + "...";
};

const expandAllHolders = (
  holders: UnitHolder[],
  fundFilter: string,
  fundNameMap: Record<string, string>,
): DisplayRow[] => {
  const rows: DisplayRow[] = [];
  let sNo = 1;

  for (const h of holders) {
    const entries = parseBalanceUnit(h.balance_unit);
    if (entries.length === 0) continue;

    for (const entry of entries) {
      if (fundFilter && entry.fund_code !== fundFilter) continue;

      const resolvedUnit = resolveFundUnit(entry);
      const unitValue = parseFloat(resolvedUnit);

      if (!resolvedUnit || isNaN(unitValue) || unitValue <= 0) continue;

      rows.push({
        s_no: sNo++,
        fund_name: fundNameMap[entry.fund_code] ?? entry.fund_code,
        folio_no: h.folio_no,
        name: h.name ?? "",
        category: h.type ?? "",
        units_held: resolvedUnit,
        acc_title: h.account_title ?? "",
        account_no: h.account_no ?? "",
        email: h.email ?? "",
        phone: h.phone || h.mobile || "",
        cnic: h.cnic ?? "",
        ntn: h.ntn ?? "",
        tax_categ: h.tax_status ?? "",
        tax_status: h.tax_exempt === "true" ? "Exempt" : "Non-Exempt",
        zakat_status: h.zakat_exempt === "true" ? "Exempt" : "Non-Exempt",
        acc_taxfiler: h.tax_status ?? "",
        dividend_option:
          h.reinvest === true || h.reinvest === "true" ? "Reinvest" : "Payout",
        cgt_status: "",
        account_status: h.status ?? "",
        account_type: h.type ?? "",
        zakat_status2: h.zakat_exempt === "true" ? "Exempt" : "Non-Exempt",
        risk: h.risk_factor ?? "",
        address: h.address ?? "",
        province: h.province ?? "",
        city: h.city ?? "",
        _raw: h,
      });
    }
  }

  return rows;
};

const rowToCsvLine = (row: DisplayRow): string =>
  [
    row.s_no,
    row.fund_name,
    row.folio_no,
    row.name,
    row.category,
    row.units_held,
    row.acc_title,
    row.account_no,
    row.email,
    row.phone,
    row.cnic,
    row.ntn,
    row.tax_categ,
    row.tax_status,
    row.zakat_status,
    row.acc_taxfiler,
    row.dividend_option,
    row.cgt_status,
    row.account_status,
    row.account_type,
    row.zakat_status2,
    row.risk,
    row.address,
    row.province,
    row.city,
  ]
    .map(escapeCsvCell)
    .join(",");

// ordered column array for exports
const FIXED_ORDER = [
  "S.No",
  "Fund Name",
  "Folio #",
  "Name",
  "Category",
  "Units Held",
  "ACC_TITLE",
  "Account No",
  "Email",
  "Phone #",
  "CNIC",
  "NTN",
  "Tax Categ.",
  "Tax Status",
  "Zakat Status",
  "ACC_TAXFILER",
  "Dividend Option",
  "CGT Status",
  "Account Status",
  "Account Type",
  "Risk",
  "Address",
  "Province",
  "City",
];

const COL_TO_ROW_KEY: Record<string, keyof DisplayRow> = {
  "S.No": "s_no",
  "Fund Name": "fund_name",
  "Folio #": "folio_no",
  Name: "name",
  Category: "category",
  "Units Held": "units_held",
  ACC_TITLE: "acc_title",
  "Account No": "account_no",
  Email: "email",
  "Phone #": "phone",
  CNIC: "cnic",
  NTN: "ntn",
  "Tax Categ.": "tax_categ",
  "Tax Status": "tax_status",
  "Zakat Status": "zakat_status",
  ACC_TAXFILER: "acc_taxfiler",
  "Dividend Option": "dividend_option",
  "CGT Status": "cgt_status",
  "Account Status": "account_status",
  "Account Type": "account_type",
  Risk: "risk",
  Address: "address",
  Province: "province",
  City: "city",
};

// Main Component
const UnitHolderSummary: React.FC = () => {
  const history = useHistory();
  const { customStyles } = useCustomStylesDropDown();

  const email = sessionStorage.getItem("email") || "";
  const fund_code_session = JSON.parse(
    sessionStorage.getItem("fund_code") || "[]",
  );

  // State
  const [initialLoading, setInitialLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [loadingFunds, setLoadingFunds] = useState(false);
  const [allHolders, setAllHolders] = useState<UnitHolder[]>([]);
  const [fundNameMap, setFundNameMap] = useState<Record<string, string>>({});
  const [fundOptions, setFundOptions] = useState<FundOption[]>([]);

  const [selectedFund, setSelectedFund] =
    useState<FundOption>(ALL_FUNDS_OPTION);

  const [visibleCount, setVisibleCount] = useState(DISPLAY_PAGE_SIZE);

  // ReportTemplate state
  const [selectedValues, setSelectedValues] = useState<any>({
    heading: "",
    sub_heading: "",
    columns: COLUMN_HEADING.map((col) => col.value),
    balance: [],
  });
  const [fontSizeVal, setFontSizeVal] = useState<any>(14);
  const [paperSizeVal, setPaperSizeVal] = useState("A4");
  const [layoutVal, setLayoutVal] = useState("Landscape");
  const [marginPDF, setMarginPDF] = useState("1cm");
  const [scalePDF, setScalePDF] = useState(0.5);
  const [screenSizePerPdf, setScreenSizePerPdf] = useState<any>();
  const [dimensionOfPages, setDimensionOfPages] = useState<any>();

  const contentRef = useRef<HTMLDivElement>(null);
  const allHoldersRef = useRef<UnitHolder[]>([]);
  const fundNameMapRef = useRef<Record<string, string>>({});
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Hide the "Print" option rendered by ReportTemplates.
  useEffect(() => {
    const hidePrintItem = () => {
      if (!wrapperRef.current) return;
      const items = wrapperRef.current.querySelectorAll<HTMLElement>(
        "li, button, a, div[role='menuitem'], span",
      );
      items.forEach((el) => {
        if (el.textContent?.trim().toLowerCase() === "print") {
          el.style.setProperty("display", "none", "important");
          const li = el.closest("li");
          if (li) li.style.setProperty("display", "none", "important");
        }
      });
    };

    hidePrintItem();

    const observer = new MutationObserver(hidePrintItem);
    if (wrapperRef.current) {
      observer.observe(wrapperRef.current, { childList: true, subtree: true });
    }
    return () => observer.disconnect();
  }, []);

  const filteredRows = useMemo(
    () => expandAllHolders(allHolders, selectedFund?.value ?? "", fundNameMap),
    [allHolders, selectedFund, fundNameMap],
  );

  const displayRows = useMemo(
    () => filteredRows.slice(0, visibleCount),
    [filteredRows, visibleCount],
  );

  const hasMore = visibleCount < filteredRows.length;

  useEffect(() => {
    setVisibleCount(DISPLAY_PAGE_SIZE);
  }, [filteredRows]);

  // Effects
  useEffect(() => {
    bulkLoadAllHolders().then((holders) => fetchAndBuildFundOptions(holders));
  }, []);

  useEffect(() => {
    allHoldersRef.current = allHolders;
  }, [allHolders]);
  useEffect(() => {
    fundNameMapRef.current = fundNameMap;
  }, [fundNameMap]);

  useEffect(() => {
    const elements = document.querySelectorAll(
      "#uhsTest",
    ) as NodeListOf<HTMLElement>;
    elements.forEach((el) => {
      el.style.setProperty("font-size", `${fontSizeVal}px`, "important");
    });
  }, [fontSizeVal, selectedValues]);

  // Data loading
  const bulkLoadAllHolders = async (): Promise<UnitHolder[]> => {
    setInitialLoading(true);
    let collected: UnitHolder[] = [];

    try {
      const firstResp = await getAllUnitHolders(email, 1, BULK_PAGE_SIZE, {
        email,
        ...(fund_code_session?.length > 0
          ? { fund_code: fund_code_session }
          : {}),
      });

      if (firstResp?.data?.status !== 200) {
        toast.error(firstResp?.data?.message ?? "Failed to load data.");
        setInitialLoading(false);
        return collected;
      }

      const firstPage: UnitHolder[] = firstResp.data.data ?? [];
      const totalBackendPages: number =
        firstResp.data.page_info?.totalPages ?? 1;
      collected = [...firstPage];

      const remainingPages = Array.from(
        { length: totalBackendPages - 1 },
        (_, i) => i + 2,
      );

      for (let i = 0; i < remainingPages.length; i += LOAD_CONCURRENCY) {
        const batch = remainingPages.slice(i, i + LOAD_CONCURRENCY);
        const results = await Promise.all(
          batch.map((p) =>
            getAllUnitHolders(email, p, BULK_PAGE_SIZE, {
              email,
              ...(fund_code_session?.length > 0
                ? { fund_code: fund_code_session }
                : {}),
            }).catch(() => null),
          ),
        );
        for (const res of results) {
          if (res?.data?.status === 200 && res.data.data?.length > 0) {
            collected.push(...res.data.data);
          }
        }
      }

      collected = collected.filter((h) => {
        const entries = parseBalanceUnit(h.balance_unit);
        return entries.some((entry) => {
          const resolvedUnit = resolveFundUnit(entry);
          const unitValue = parseFloat(resolvedUnit);
          return resolvedUnit && !isNaN(unitValue) && unitValue > 0;
        });
      });

      setAllHolders(collected);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to load data.");
    }

    setInitialLoading(false);
    return collected;
  };

  const fetchAndBuildFundOptions = async (holders: UnitHolder[]) => {
    setLoadingFunds(true);
    try {
      const activeFundCodes = new Set<string>();
      for (const h of holders) {
        for (const entry of parseBalanceUnit(h.balance_unit)) {
          const resolvedUnit = resolveFundUnit(entry);
          const unitValue = parseFloat(resolvedUnit);
          if (
            entry.fund_code &&
            resolvedUnit &&
            !isNaN(unitValue) &&
            unitValue > 0
          ) {
            activeFundCodes.add(entry.fund_code);
          }
        }
      }

      const response = await getFunds({
        email,
        page_number: "1",
        page_size: "1000",
        fund_page: "true",
        parent_fund: "",
      });

      if (response.data.data?.length > 0) {
        const funds = response.data.data;

        const nameMap: Record<string, string> = {};
        for (const fund of funds) {
          nameMap[fund.symbol_code] = fund.fund_name;
        }

        const nameCounts: Record<string, number> = {};
        for (const fund of funds) {
          if (activeFundCodes.has(fund.symbol_code)) {
            nameCounts[fund.fund_name] = (nameCounts[fund.fund_name] ?? 0) + 1;
          }
        }

        const knownCodes = new Set(funds.map((f: any) => f.symbol_code));

        const fundSpecificOptions: FundOption[] = funds
          .filter((fund: any) => activeFundCodes.has(fund.symbol_code))
          .map((fund: any) => ({
            value: fund.symbol_code,
            label:
              nameCounts[fund.fund_name] > 1
                ? `${fund.fund_name} (${fund.symbol_code})`
                : fund.fund_name,
          }));

        for (const code of Array.from(activeFundCodes)) {
          if (!knownCodes.has(code)) {
            nameMap[code] = code;
            fundSpecificOptions.push({ value: code, label: code });
          }
        }

        fundSpecificOptions.sort((a: FundOption, b: FundOption) =>
          a.label.localeCompare(b.label),
        );

        setFundNameMap(nameMap);
        setFundOptions([ALL_FUNDS_OPTION, ...fundSpecificOptions]);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? "Failed to load funds.");
    }
    setLoadingFunds(false);
  };

  // Export helpers
  const getOrderedColumns = (): string[] =>
    FIXED_ORDER.filter((col) => selectedValues?.columns?.includes(col));

  const buildArrayData = (rows: DisplayRow[]) => {
    const orderedCols = getOrderedColumns();
    return rows.map((row) => {
      const result: Record<string, any> = {};
      orderedCols.forEach((col) => {
        const key = COL_TO_ROW_KEY[col];
        result[col] = key ? row[key] : "";
      });
      return result;
    });
  };

  const ensureAllHolders = async (): Promise<UnitHolder[]> => {
    if (allHoldersRef.current.length > 0) return allHoldersRef.current;

    toast.info("Re-fetching data for export…", { autoClose: 2000 });
    const firstResp = await getAllUnitHolders(email, 1, BULK_PAGE_SIZE, {
      email,
    });
    if (firstResp?.data?.status !== 200) throw new Error("Export failed.");

    const totalBkPages: number = firstResp.data.page_info?.totalPages ?? 1;
    const holders: UnitHolder[] = [...(firstResp.data.data ?? [])];
    const remaining = Array.from({ length: totalBkPages - 1 }, (_, i) => i + 2);

    for (let i = 0; i < remaining.length; i += EXPORT_CONCURRENCY) {
      const batch = remaining.slice(i, i + EXPORT_CONCURRENCY);
      const results = await Promise.all(
        batch.map((p) =>
          getAllUnitHolders(email, p, BULK_PAGE_SIZE, { email }).catch(
            () => null,
          ),
        ),
      );
      for (const res of results) {
        if (res?.data?.status === 200 && res.data.data?.length > 0)
          holders.push(...res.data.data);
      }
    }
    setAllHolders(holders);
    return holders;
  };

  const handleExportExcel = useCallback(async () => {
    if (selectedValues?.columns?.length === 0) {
      toast.error("Please select at least one column.");
      return;
    }
    setExporting(true);
    try {
      const holders = await ensureAllHolders();
      const allRows = expandAllHolders(
        holders,
        selectedFund?.value ?? "",
        fundNameMapRef.current,
      );
      if (allRows.length === 0) {
        toast.info("No investors found to export.");
        return;
      }
      const orderedCols = getOrderedColumns();
      const arrayData = buildArrayData(allRows);

      generateExcel(
        "Unit Holder Summary",
        "Unit Holder Summary",
        "File",
        "File",
        "DCCL",
        [],
        orderedCols,
        arrayData,
        [],
        [],
      );
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Export failed.");
    }
    setExporting(false);
  }, [selectedValues, selectedFund, email]);

  const handleExportTxt = useCallback(async () => {
    if (selectedValues?.columns?.length === 0) {
      toast.error("Please select at least one column.");
      return;
    }
    setExporting(true);
    try {
      const holders = await ensureAllHolders();
      const allRows = expandAllHolders(
        holders,
        selectedFund?.value ?? "",
        fundNameMapRef.current,
      );
      if (allRows.length === 0) {
        toast.info("No investors found to export.");
        return;
      }
      const arrayData = buildArrayData(allRows);
      exportToTxt("", selectedValues, arrayData, "Unit Holder Summary");
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Export failed.");
    }
    setExporting(false);
  }, [selectedValues, selectedFund, email]);

  const handleShowMore = () => {
    setVisibleCount((prev) =>
      Math.min(prev + DISPLAY_PAGE_SIZE, filteredRows.length),
    );
  };

  const totalCount = filteredRows.length;

  const renderShowMoreBar = () => {
    if (filteredRows.length === 0) return null;
    return (
      <div
        className="d-flex justify-content-between align-items-center mt-4"
        style={{ gap: "1rem" }}
      >
        <span
          className="border rounded px-3 py-1"
          style={{
            fontSize: "0.85rem",
            whiteSpace: "nowrap",
            color: "black",
            fontWeight: 600,
          }}
        >
          Per page : {DISPLAY_PAGE_SIZE}
        </span>

        <div className="flex-grow-1 text-center">
          {hasMore && (
            <button
              className="btn btn-primary px-4 py-2"
              style={{
                background: "linear-gradient(rgb(225, 78, 202))",
                border: "none",
                borderRadius: "10px",
                fontWeight: 600,
                letterSpacing: "0.03em",
                boxShadow: "0 4px 14px rgba(168,85,247,0.35)",
              }}
              onClick={handleShowMore}
            >
              Show More
            </button>
          )}
        </div>

        <span
          className="border rounded px-3 py-1"
          style={{
            fontSize: "0.85rem",
            whiteSpace: "nowrap",
            color: "black",
            fontWeight: 600,
          }}
        >
          Total record : {totalCount}
        </span>
      </div>
    );
  };

  // Table body
  const renderTableRows = () =>
    displayRows.map((row, i) => (
      <tr key={`${row.folio_no}-${row.fund_name}-${i}`}>
        {selectedValues?.columns?.includes("S.No") && (
          <td id="uhsTest">{row.s_no}</td>
        )}
        {selectedValues?.columns?.includes("Fund Name") && (
          <td id="uhsTest" style={{ minWidth: "180px" }}>
            {row.fund_name}
          </td>
        )}
        {selectedValues?.columns?.includes("Folio #") && (
          <td id="uhsTest">{row.folio_no}</td>
        )}
        {selectedValues?.columns?.includes("Name") && (
          <td id="uhsTest" style={{ minWidth: "160px" }}>
            {row.name || "-"}
          </td>
        )}
        {selectedValues?.columns?.includes("Category") && (
          <td id="uhsTest">{row.category || "-"}</td>
        )}
        {selectedValues?.columns?.includes("Units Held") && (
          <td id="uhsTest">
            {parseFloat(row.units_held || "0").toLocaleString()}
          </td>
        )}
        {selectedValues?.columns?.includes("ACC_TITLE") && (
          <td id="uhsTest" style={{ minWidth: "160px" }}>
            {row.acc_title || "-"}
          </td>
        )}
        {selectedValues?.columns?.includes("Account No") && (
          <td id="uhsTest" style={{ minWidth: "150px" }}>
            {row.account_no || "-"}
          </td>
        )}
        {selectedValues?.columns?.includes("Email") && (
          <td id="uhsTest" style={{ minWidth: "160px" }}>
            {row.email || "-"}
          </td>
        )}
        {selectedValues?.columns?.includes("Phone #") && (
          <td id="uhsTest">{row.phone || "-"}</td>
        )}
        {selectedValues?.columns?.includes("CNIC") && (
          <td id="uhsTest">{row.cnic}</td>
        )}
        {selectedValues?.columns?.includes("NTN") && (
          <td id="uhsTest">{row.ntn || "-"}</td>
        )}
        {selectedValues?.columns?.includes("Tax Categ.") && (
          <td id="uhsTest">{row.tax_categ || "-"}</td>
        )}
        {selectedValues?.columns?.includes("Tax Status") && (
          <td id="uhsTest">{row.tax_status}</td>
        )}
        {selectedValues?.columns?.includes("Zakat Status") && (
          <td id="uhsTest">{row.zakat_status}</td>
        )}
        {selectedValues?.columns?.includes("ACC_TAXFILER") && (
          <td id="uhsTest">{row.acc_taxfiler || "-"}</td>
        )}
        {selectedValues?.columns?.includes("Dividend Option") && (
          <td id="uhsTest">{row.dividend_option}</td>
        )}
        {selectedValues?.columns?.includes("CGT Status") && (
          <td id="uhsTest">{row.cgt_status || "-"}</td>
        )}
        {selectedValues?.columns?.includes("Account Status") && (
          <td id="uhsTest">
            <span
              className={`badge ${row.account_status === "active" ? "badge-success" : "badge-danger"}`}
              style={{ fontSize: "0.78rem" }}
            >
              {row.account_status}
            </span>
          </td>
        )}
        {selectedValues?.columns?.includes("Account Type") && (
          <td id="uhsTest">{row.account_type || "-"}</td>
        )}
        {selectedValues?.columns?.includes("Risk") && (
          <td id="uhsTest">{row.risk || "-"}</td>
        )}
        {selectedValues?.columns?.includes("Address") && (
          <td
            id="uhsTest"
            style={{ minWidth: "180px" }}
            title={row.address || ""}
          >
            {truncateAddress(row.address)}
          </td>
        )}
        {selectedValues?.columns?.includes("Province") && (
          <td id="uhsTest">{row.province || "-"}</td>
        )}
        {selectedValues?.columns?.includes("City") && (
          <td id="uhsTest">{row.city || "-"}</td>
        )}
      </tr>
    ));

  //Render
  return (
    <div className="content">
      <style>{`
        @media screen {
          table.screen-table th,
          table.screen-table td {
            padding-left: 16px !important;
            padding-right: 16px !important;
            white-space: nowrap;
          }
        }
        @media print {
          table.screen-table th,
          table.screen-table td {
            padding-left: 4px !important;
            padding-right: 4px !important;
            white-space: normal;
          }
        }
        /* Hide the Print option from the ReportTemplates export dropdown */
        .uhs-wrapper li.print-option,
        .uhs-wrapper [data-action="print"] {
          display: none !important;
        }
      `}</style>
      <Row>
        <Col md="12">
          <Container fluid>
            <ToastContainer limit={1} />

            <div className="card">
              <div className="card-header">
                <h4 className="card-title">Unit Holder Summary</h4>
              </div>

              <div className="card-body">
                <div ref={wrapperRef}>
                  <Row className="align-items-end mb-3">
                    <Col md="4">
                      <div className="form-group mb-0">
                        <label className="d-flex align-items-center">
                          <FaFilter className="mr-2 text-primary" />
                          Filter by Fund
                        </label>
                        <Select
                          value={selectedFund}
                          onChange={(opt) =>
                            setSelectedFund(opt ?? ALL_FUNDS_OPTION)
                          }
                          menuPortalTarget={document.body}
                          menuShouldBlockScroll={true}
                          isClearable={false}
                          isSearchable={true}
                          isLoading={loadingFunds}
                          options={fundOptions}
                          styles={customStyles}
                          placeholder="All"
                          isDisabled={initialLoading}
                        />
                      </div>
                    </Col>
                  </Row>

                  {!initialLoading && filteredRows.length > 0 && (
                    <Row>
                      <ReportTemplates
                        ColumnArr={COLUMN_HEADING}
                        selectedValues={selectedValues}
                        setSelectedValues={setSelectedValues}
                        pdfExportComponent={{ current: null }}
                        setFontSizeVal={setFontSizeVal}
                        fontSizeVal={fontSizeVal}
                        setPaperSizeVal={setPaperSizeVal}
                        paperSizeVal={paperSizeVal}
                        setLayoutVal={setLayoutVal}
                        layoutVal={layoutVal}
                        setScalePDF={setScalePDF}
                        scalePDF={scalePDF}
                        setMarginPDF={setMarginPDF}
                        marginPDF={marginPDF}
                        setScreenSizePerPdf={setScreenSizePerPdf}
                        screenSizePerPdf={screenSizePerPdf}
                        setDimensionOfPages={setDimensionOfPages}
                        contentRef={contentRef}
                        fileName={"UnitHolderSummary.pdf"}
                        printExcel={handleExportExcel}
                        printTXT={handleExportTxt}
                      />
                    </Row>
                  )}

                  {initialLoading ? (
                    <div className="d-flex justify-content-center my-5">
                      <i
                        className="fa fa-spinner fa-spin fa-3x"
                        aria-hidden="true"
                      />
                    </div>
                  ) : displayRows.length > 0 ? (
                    <>
                      <div
                        id="printDiv"
                        className="p-3 mt-3"
                        style={{ overflowX: "auto", width: "auto" }}
                      >
                        <div
                          style={{
                            width: `${
                              dimensionOfPages?.find(
                                (item: any) => item?.key === paperSizeVal,
                              )?.dimensions[layoutVal === "Portrait" ? 0 : 1]
                            }`,
                            height: "auto",
                            margin: "0 auto",
                            backgroundColor: "white",
                          }}
                          className="border"
                        >
                          <div style={{ margin: "0.25in" }} ref={contentRef}>
                            <ReportHeader
                              title="Unit Holder Summary"
                              reportDate={moment().format("DD-MMM-YYYY")}
                            />

                            {selectedValues?.columns?.length > 0 ? (
                              <div
                                style={{
                                  overflowX: "auto",
                                  overflowY: "hidden",
                                }}
                              >
                                <table
                                  className="report screen-table"
                                  style={{ color: "black" }}
                                >
                                  <thead
                                    style={{
                                      borderTop: "2px solid #000",
                                      borderBottom: "2px solid #000",
                                    }}
                                  >
                                    <tr>
                                      {selectedValues?.columns?.map(
                                        (col: string, idx: number) => (
                                          <th key={idx} id="uhsTest">
                                            {col}
                                          </th>
                                        ),
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody className="report-table-body">
                                    {renderTableRows()}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="text-center my-5">
                                <b>No column selected</b>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {renderShowMoreBar()}
                    </>
                  ) : (
                    <div className="table-empty">
                      <BsTable className="icon" />
                      <p className="text-center empty-text">
                        {selectedFund && selectedFund.value !== ""
                          ? `No investors found for "${selectedFund.label}".`
                          : "No unit holders with fund investments found."}
                      </p>
                    </div>
                  )}
                </div>{" "}
              </div>
            </div>
          </Container>
        </Col>
      </Row>
    </div>
  );
};

export default UnitHolderSummary;
